import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FacetConfig, resolveTermName } from '../GovernanceAnalyticsAiVisibility.utils';
import { BAND_ICON_BOX, BandThresholdAxisIcon } from './BandThresholdAxisIcon';
import { BrandLandscapeTile } from './BrandLandscapeTile';
import {
  BRAND_QUADRANTS,
  BrandLandscapeRow,
  BrandQuadrant,
  GAPS_REFRESH_FAILED_MESSAGE,
  LandscapeBrand,
  PlottedBrand,
  QUADRANT_MIDPOINT,
  QUADRANT_RULES,
  buildTileColors,
  chipOrder,
  facetOfTerm,
  hoverTextFor,
  isPlotted,
  isPressed,
  layoutBrands,
  missingFigureNotice,
  notPlottedLines,
  overlappingKeys,
  plotAccessibleLabel,
  plottableBrands,
  reconcileSelection,
  sentimentPlotDomain,
  tagsInViewLine,
} from './BrandLandscapeCard.utils';
import { bandThresholdMarkers, TONE_BAND_METRIC } from './bandVisuals.utils';
import { TONE_METRIC_LABEL } from '../toneMetric';
import { GovernanceAnalyticsSectionCard } from './GovernanceAnalyticsSectionCard';
import { GOVERNANCE_COMPARISON_PALETTE, readPrimaryColor } from './governanceThemeColors';
import { SectionLoadFailed } from './SectionLoadFailed';
import { StatsRequestPriority } from './statsRequestQueue';
import { EntityDomainMap, useEntityDomains } from './useEntityDomains';

/** The edge length of a tile on the plot, and the separation the collision rule works in. */
const TILE_SIZE = 28;
/** The smaller mark a chip carries beside the brand's name. */
const CHIP_TILE_SIZE = 16;

/**
 * The one icon size this card asks for, whatever size it draws the mark at — snapped up to a size
 * Sync Core actually serves on the way out (`faviconRequestSize`).
 *
 * Both marks share it deliberately. Sync Core fetches an icon it has not cached before on the
 * request that asks for it, and the page-wide favicon queue is four slots wide; asking for two sizes
 * of the same logo would double this card's cold fetches for no visible gain, with the Sources card
 * below competing for the same four slots. Twice the plot tile, so the larger of the two marks is
 * the one that stays crisp.
 */
const TILE_FAVICON_REQUEST_SIZE = TILE_SIZE * 2;

/**
 * Gutters around the plotted area. The left one holds the Sentiment band glyphs and the axis name,
 * the bottom one the Visibility ticks and that axis's name.
 */
const PLOT_MARGIN = { top: 20, right: 30, bottom: 44, left: 64 };

/**
 * Where a band glyph's box starts in the Sentiment gutter, and how wide it is.
 *
 * The box ends two pixels short of the axis so it can never sit over the plot: it is a
 * `foreignObject`, and one overlapping the plotted area would take the hover of any tile beneath it.
 */
const SENTIMENT_ICON_X = 32;
const SENTIMENT_ICON_BOX_WIDTH = 30;

/** How far the rotated axis name sits from the left edge of its gutter. */
const SENTIMENT_LABEL_INSET = 12;

/** The boundaries the Sentiment axis is labelled by. The card holds no thresholds of its own. */
const SENTIMENT_THRESHOLDS = bandThresholdMarkers(TONE_BAND_METRIC);

/**
 * The smallest plot the axes and a tile still fit in.
 *
 * Below it the card clips rather than redraws the plot at a size where the axes would collide: a
 * squeezed picture of a measurement is harder to catch being wrong than a cut-off one.
 */
const MIN_PLOT_EXTENT = 120;

/**
 * Which corner of the DRAWN area each quadrant's name sits in.
 *
 * All four are always painted, in the corners of whatever the plot currently shows. They are the
 * reader's compass — up is spoken of better, right is seen more often — and a compass with two of
 * its points missing is worse than no compass, because the two that remain look like the whole of
 * it. Which brands actually fall in each is what the tiles and their hovers say.
 */
const QUADRANT_CORNERS: Record<BrandQuadrant, { right: boolean; bottom: boolean }> = {
  'Niche Players': { right: false, bottom: false },
  Leaders: { right: true, bottom: false },
  Laggers: { right: false, bottom: true },
  Controversial: { right: true, bottom: true },
};

const QUADRANT_LABEL_INSET = 8;

/**
 * Every tracked brand placed by how often it is seen against how it is spoken of.
 *
 * A second reader of the rows the Gaps table below already fetched: the card adds no stats request
 * of its own, and the chips filter what those rows contain rather than driving what is fetched.
 * Opting a kind of tag in or out is what the Gaps buttons are for, which is what the header line
 * says.
 *
 * The one request it does make is the entity→domain lookup for the logos, and it lives inside this
 * component rather than on the screen so that a purpose whose KPI set hides this card never makes it.
 */
export function BrandLandscapeCard({
  api,
  rows,
  facetConfigs,
  facetNames,
  loading,
  error,
  onRetry,
  purposeTermKey,
}: {
  api?: ISyncCoreApi;
  rows: readonly BrandLandscapeRow[];
  facetConfigs: FacetConfig[];
  /** The tags in view, by the names the Gaps buttons show for them. */
  facetNames: readonly string[];
  loading?: boolean;
  /** The rows could not be refreshed. The card says so rather than reading as measuring nothing. */
  error?: boolean;
  /** Asks for the rows again, offered beside the message when they could not be refreshed. */
  onRetry?: () => void;
  purposeTermKey?: string;
}) {
  // On the AI Visibility screen, so the lookup takes its turn with the screen's other statistics.
  const domains = useEntityDomains(api, purposeTermKey, { queuePriority: StatsRequestPriority.Landscape });

  const brands = useMemo(
    () =>
      plottableBrands(rows, {
        facetOf: (key) => facetOfTerm(facetConfigs, key),
        nameOf: (key) => resolveTermName(facetConfigs, key),
      }),
    [rows, facetConfigs]
  );

  /** The brands the reader has switched off. Everything else is pressed, including new arrivals. */
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set<string>());

  useEffect(() => {
    setHidden((previous) => {
      const next = reconcileSelection(previous, brands);
      // Reconciling only ever drops keys, so an unchanged size is an unchanged set — and returning
      // the same reference is what stops this effect from feeding itself.
      return next.size === previous.size ? previous : next;
    });
  }, [brands]);

  const primaryColor = useMemo(() => readPrimaryColor(), []);
  const tileColors = useMemo(
    () =>
      buildTileColors(brands, {
        primaryColor,
        palette: GOVERNANCE_COMPARISON_PALETTE,
        // A brand with a domain is a brand whose tile is expected to draw a logo instead of a
        // colour, so it is dealt from the palette last and leaves the distinct hues to the tiles
        // that have nothing but colour and two letters to tell them apart.
        hasLogo: (key) => !!domains.get(key),
      }),
    [brands, primaryColor, domains]
  );

  /** The strip, most visible brand first — the plot is unaffected, it sorts by tag key itself. */
  const chips = useMemo(() => chipOrder(brands), [brands]);
  const plotted = useMemo(() => brands.filter(isPlotted), [brands]);
  const notPlotted = useMemo(() => notPlottedLines(brands), [brands]);

  const toggle = useCallback(
    (key: string) =>
      setHidden((previous) => {
        const next = new Set(previous);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      }),
    []
  );

  // Loading takes precedence over emptiness: rows that have not arrived are not rows that are not
  // there, and the card's own spinner overlay is what says which of the two the reader is looking at.
  const isEmpty = !loading && !brands.length;

  return (
    <GovernanceAnalyticsSectionCard title="Brand landscape" loading={loading}>
      <p className="mb-3 text-xs text-slate-600">{tagsInViewLine(facetNames)}</p>

      {isEmpty ? null : (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {chips.map((brand) => (
            <BrandChip
              key={`brand-landscape-chip:${brand.key}`}
              api={api}
              brand={brand}
              color={tileColors.get(brand.key) || primaryColor}
              domain={domains.get(brand.key)?.domain}
              pressed={isPressed(brand, hidden)}
              onToggle={toggle}
            />
          ))}
        </div>
      )}

      {isEmpty && error && onRetry ? (
        <SectionLoadFailed message={GAPS_REFRESH_FAILED_MESSAGE} onRetry={onRetry} />
      ) : isEmpty ? (
        <div className="text-sm text-slate-500">{error ? GAPS_REFRESH_FAILED_MESSAGE : 'No data available.'}</div>
      ) : (
        /**
         * A DEFINITE height, deliberately not `flex-1` and not a `min-h`.
         *
         * `ParentSize` renders a `height: 100%` box and measures it, and a percentage height only
         * resolves against a containing block whose own height is definite. A minimum height or a
         * flex track inside an auto-height column leaves it indefinite, the measurement comes back
         * zero, and the plot draws nothing at all.
         */
        <div className="h-[320px] overflow-hidden" data-brand-landscape-plot>
          <ParentSize>
            {({ width, height }) => (
              <LandscapePlot
                api={api}
                brands={plotted}
                hidden={hidden}
                tileColors={tileColors}
                fallbackColor={primaryColor}
                domains={domains}
                width={width}
                height={height}
              />
            )}
          </ParentSize>
        </div>
      )}

      {notPlotted.map((line) => (
        <p key={`brand-landscape-not-plotted:${line}`} className="mb-0 mt-3 text-xs text-slate-600">
          {line}
        </p>
      ))}
    </GovernanceAnalyticsSectionCard>
  );
}

/**
 * One brand in the strip: its mark, its name, and whether its tile is on the plot.
 *
 * Three states the reader can tell apart without seeing a hue: pressed carries a tick, unpressed
 * carries none, and a brand nobody has measured both figures for carries a dashed edge and no tick
 * at all. A dashed chip does nothing when clicked, and says so to a screen reader, but stays on the
 * Tab path — its hover is the only place that names which figure is missing.
 */
function BrandChip({
  api,
  brand,
  color,
  domain,
  pressed,
  onToggle,
}: {
  api?: ISyncCoreApi;
  brand: LandscapeBrand;
  color: string;
  domain?: string;
  pressed: boolean;
  onToggle: (key: string) => void;
}) {
  const plottable = isPlotted(brand);
  // A greyed chip's explanation is announced through `aria-describedby`, not left to `title` alone:
  // a `title` is not reliably read out, and this description is the one place naming which figure a
  // brand is missing. The element it points at sits OUTSIDE the button, so it describes the chip
  // without its text folding into the chip's accessible name, which stays the brand's name alone.
  const descriptionId = plottable ? undefined : `brand-landscape-missing:${brand.key}`;

  return (
    <>
      <button
        type="button"
        data-brand-chip={brand.key}
        aria-pressed={plottable ? pressed : undefined}
        aria-disabled={plottable ? undefined : true}
        aria-describedby={descriptionId}
        title={plottable ? hoverTextFor(brand) : missingFigureNotice(brand)}
        onClick={plottable ? () => onToggle(brand.key) : undefined}
        className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs ${
          plottable
            ? pressed
              ? 'border-slate-500 bg-slate-100 text-slate-900'
              : 'border-slate-400 bg-white text-slate-700 hover:bg-slate-50'
            : 'cursor-default border-dashed border-slate-400 bg-white text-slate-500'
        }`}
      >
        <BrandLandscapeTile
          api={api}
          name={brand.name}
          color={color}
          size={CHIP_TILE_SIZE}
          domain={domain}
          requestSize={TILE_FAVICON_REQUEST_SIZE}
        />
        <span>{brand.name}</span>
        {plottable && pressed ? <FontAwesomeIcon icon={faCheck} className="text-slate-600" aria-hidden /> : null}
      </button>
      {descriptionId ? (
        <span id={descriptionId} className="sr-only">
          {missingFigureNotice(brand)}
        </span>
      ) : null}
    </>
  );
}

/**
 * The axes, the midlines, the quadrant names and the tiles, over one measured box.
 *
 * Placement runs over EVERY plotted brand, not only the pressed ones, and the pressed set is filtered
 * out of the result afterwards. Laying out the pressed set alone would make each tile's position
 * depend on which chips are on, so switching one brand off would slide the tiles it had pushed aside
 * — and a tile that moves for a reason the reader did not ask for is a tile whose position they stop
 * trusting. The Sentiment window is read from that same full set, for the same reason: a window that
 * shrank to the pressed brands would restretch the whole vertical axis on every chip.
 *
 * The two axes are deliberately not symmetric. Visibility keeps the whole 0–100% scale — it is a
 * share of a population, and the distance from nothing is the reading. Sentiment is zoomed to the
 * data and labelled by its bands rather than by numbers, because sentiments cluster into a few
 * points of each other: on a fixed scale every tile lands in one horizontal stripe, and 0.62 against
 * 0.66 means nothing to a reader until the axis says which of them clears the Good boundary.
 */
function LandscapePlot({
  api,
  brands,
  hidden,
  tileColors,
  fallbackColor,
  domains,
  width,
  height,
}: {
  api?: ISyncCoreApi;
  brands: readonly PlottedBrand[];
  hidden: ReadonlySet<string>;
  tileColors: ReadonlyMap<string, string>;
  fallbackColor: string;
  domains: EntityDomainMap;
  width: number;
  height: number;
}) {
  const innerWidth = Math.max(MIN_PLOT_EXTENT, width - PLOT_MARGIN.left - PLOT_MARGIN.right);
  const innerHeight = Math.max(MIN_PLOT_EXTENT, height - PLOT_MARGIN.top - PLOT_MARGIN.bottom);

  const sentimentDomain = useMemo(() => sentimentPlotDomain(brands.map((brand) => brand.sentiment)), [brands]);

  const placed = useMemo(
    () => layoutBrands(brands, { width: innerWidth, height: innerHeight, tileSize: TILE_SIZE, sentimentDomain }),
    [brands, innerWidth, innerHeight, sentimentDomain]
  );
  const drawn = useMemo(() => placed.filter((entry) => !hidden.has(entry.brand.key)), [placed, hidden]);
  const overlapping = useMemo(() => overlappingKeys(drawn, TILE_SIZE), [drawn]);
  const label = useMemo(() => plotAccessibleLabel(drawn, overlapping), [drawn, overlapping]);

  const xScale = scaleLinear<number>({ domain: [0, 1], range: [0, innerWidth] });
  const yScale = scaleLinear<number>({ domain: [sentimentDomain.min, sentimentDomain.max], range: [innerHeight, 0] });

  // Only the boundaries inside the drawn window: one outside it has no position on this axis, and a
  // glyph pinned to an edge it does not sit on would put the reader in the wrong band.
  const visibleThresholds = SENTIMENT_THRESHOLDS.filter(
    (marker) => marker.value >= sentimentDomain.min && marker.value <= sentimentDomain.max
  );

  // Sized from the floored inner box rather than from the raw measurement, so the SVG and the tile
  // layer above it never disagree about where the plot is in a card too narrow for the full width.
  const svgWidth = innerWidth + PLOT_MARGIN.left + PLOT_MARGIN.right;
  const svgHeight = innerHeight + PLOT_MARGIN.top + PLOT_MARGIN.bottom;

  return (
    <div className="relative h-full w-full">
      <svg width={svgWidth} height={svgHeight} role="img" aria-label={label}>
        <Group left={PLOT_MARGIN.left} top={PLOT_MARGIN.top}>
          <rect width={innerWidth} height={innerHeight} fill="#F8FAFC" rx={4} />

          {/* Where each Sentiment band begins, in the lighter ink every banded chart here draws it
            in — behind the tiles, and behind the quadrant splits, which are the darker pair. The
            glyph naming each one is on the axis, not here. */}
          {visibleThresholds.map((marker) => (
            <line
              key={`brand-landscape-band:${marker.tone}`}
              x1={0}
              x2={innerWidth}
              y1={yScale(marker.value)}
              y2={yScale(marker.value)}
              stroke="#CBD5E1"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
          ))}

          {/*
           * Halfway along Visibility, and the only split the plot draws.
           *
           * Visibility is a share of a population, so half of it is a landmark on its own: left of
           * this line a brand is seen in a minority of answers, right of it a majority. Sentiment
           * carries no equivalent — its landmarks are the band boundaries above, which is what the
           * dashed lines across the plot already are, and a second horizontal line at some arbitrary
           * 0.5 would read as one more of them while meaning nothing.
           */}
          <line
            x1={xScale(QUADRANT_MIDPOINT)}
            x2={xScale(QUADRANT_MIDPOINT)}
            y1={0}
            y2={innerHeight}
            stroke="#94A3B8"
            strokeWidth={1}
            strokeDasharray="4 4"
          />

          {BRAND_QUADRANTS.map((quadrant) => {
            const { right, bottom } = QUADRANT_CORNERS[quadrant];
            return (
              <text
                key={`brand-landscape-quadrant:${quadrant}`}
                data-brand-quadrant={quadrant}
                x={right ? innerWidth - QUADRANT_LABEL_INSET : QUADRANT_LABEL_INSET}
                y={bottom ? innerHeight - QUADRANT_LABEL_INSET : QUADRANT_LABEL_INSET + 10}
                textAnchor={right ? 'end' : 'start'}
                className="fill-slate-600"
                fontSize={11}
              >
                <title>{QUADRANT_RULES[quadrant]}</title>
                {quadrant}
              </text>
            );
          })}

          <AxisBottom
            top={innerHeight}
            scale={xScale}
            label="Visibility"
            tickValues={[0, 0.25, 0.5, 0.75, 1]}
            tickFormat={(value) => `${Math.round(Number(value) * 100)}%`}
            stroke="#64748B"
            tickStroke="#64748B"
            labelProps={{ fill: '#475569', fontSize: 11, textAnchor: 'middle' }}
            tickLabelProps={() => ({ fill: '#475569', fontSize: 10, textAnchor: 'middle', dy: '0.25em' })}
          />
          {/*
           * A banded axis, the way every other banded y-axis on these screens is drawn: its ticks
           * are the band boundaries and nothing else, and each is labelled by its glyph. A number
           * here would be a second kind of label on one axis, and the reader would have to decide
           * which of the two is the landmark — on a window that is zoomed, a bare "0.6" is also the
           * least useful of the two, since what it answers is "which band am I in".
           */}
          <AxisLeft
            scale={yScale}
            tickValues={visibleThresholds.map((marker) => marker.value)}
            tickFormat={() => ''}
            stroke="#64748B"
            tickStroke="#64748B"
          />
          {visibleThresholds.map((marker) => (
            <foreignObject
              key={`brand-landscape-band-icon:${marker.tone}`}
              data-brand-band-icon={marker.tone}
              x={-SENTIMENT_ICON_X}
              y={yScale(marker.value) - BAND_ICON_BOX / 2}
              width={SENTIMENT_ICON_BOX_WIDTH}
              height={BAND_ICON_BOX}
              style={{ overflow: 'visible' }}
            >
              <span
                className="inline-flex items-center"
                style={{ height: BAND_ICON_BOX }}
                title={`${marker.label} from ${marker.percent}% — ${marker.hint}`}
              >
                <BandThresholdAxisIcon glyph={marker.glyph} />
              </span>
            </foreignObject>
          ))}
          {/* The axis name, placed here rather than through the axis's own `label`, which measures
            its offset from tick text this axis deliberately has none of. */}
          <text
            transform={`translate(${SENTIMENT_LABEL_INSET - PLOT_MARGIN.left}, ${innerHeight / 2}) rotate(-90)`}
            textAnchor="middle"
            fill="#475569"
            fontSize={11}
          >
            {TONE_METRIC_LABEL}
          </text>
        </Group>
      </svg>

      {/*
       * The tiles are HTML over the same box rather than marks inside the SVG: each is an image with
       * letters under it, and the pure module hands both layers one coordinate system so they cannot
       * drift apart. Hidden from assistive technology because every figure on it, overlap included,
       * is already in the plot's own label.
       */}
      <div
        aria-hidden
        className="absolute"
        style={{ left: PLOT_MARGIN.left, top: PLOT_MARGIN.top, width: innerWidth, height: innerHeight }}
      >
        {drawn.map((entry) => (
          <span
            key={`brand-landscape-tile:${entry.brand.key}`}
            data-brand-tile={entry.brand.key}
            data-overlapping={overlapping.has(entry.brand.key) ? 'true' : undefined}
            title={hoverTextFor(entry.brand)}
            className="absolute"
            style={{ left: entry.x, top: entry.y }}
          >
            <BrandLandscapeTile
              api={api}
              name={entry.brand.name}
              color={tileColors.get(entry.brand.key) || fallbackColor}
              size={TILE_SIZE}
              domain={domains.get(entry.brand.key)?.domain}
              requestSize={TILE_FAVICON_REQUEST_SIZE}
              overlapping={overlapping.has(entry.brand.key)}
            />
          </span>
        ))}
      </div>
    </div>
  );
}
